"use client";

/**
 * ADR 0418 D6/D7 — learns which organisation this hostname belongs to and shares it with the app.
 * plan: 4-Archives/tms/deeptrack-plan/0418-one-web-brand-by-hostname.md (mockup frames 1 · 2 · 4 · 5)
 *
 * Starts as `loading` on both server and client (static export has no hostname at build time,
 * and identical first renders avoid a hydration mismatch), then:
 *   cache (if any) → shown at once · fetch → `resolveBrandState` → shown / cached / redirected.
 * The ONLY place that calls `location.replace` for an unknown host — and only when
 * `resolveBrandState` says `redirect` (a real HOST_NOT_FOUND from our API).
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { brandCacheKey, normalizeHost } from "@9nerd/shared";
import { resolveBrandState, serializeBrandForCache, type BrandState } from "@/lib/brand-state";
import { fetchBrand } from "@/lib/brand-api";
import { DEV_HOST_RULE_OFF_KEY, isPlainDevHostActive } from "@/lib/host-brand";
import { BrandHead } from "./BrandHead";

/** Outside a provider (unit renders of single components) = neutral: the bundle's own branding. */
const BrandContext = createContext<BrandState>({ kind: "neutral" });

export function useBrandState(): BrandState {
  return useContext(BrandContext);
}

/** ADR 0418 D15 — true on plain localhost (the org bar + switcher always show there). False until known. */
const PlainDevHostContext = createContext(false);

export function usePlainDevHost(): boolean {
  return useContext(PlainDevHostContext);
}

function readCache(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // private mode / blocked storage — the page still works, just without a head start
  }
}

function writeCache(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — nothing to do */
  }
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BrandState>({ kind: "loading" });
  const [plainDevHost, setPlainDevHost] = useState(false);

  useEffect(() => {
    // The hostname and localStorage exist only in the browser, and the first render must match the
    // static HTML (no hostname at build time) — so these reads happen here, not in useState
    // initialisers. Same reasoning as hooks/use-org-ui.ts.
    const host = normalizeHost(window.location.hostname);
    if (!host) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState({ kind: "neutral" });
      return;
    }
    setPlainDevHost(isPlainDevHostActive(host, readCache(DEV_HOST_RULE_OFF_KEY)));
    const key = brandCacheKey(host);
    const cached = readCache(key);
    setState(resolveBrandState({ host, response: { status: "pending" }, cached }));

    let alive = true;
    void fetchBrand(host).then((response) => {
      if (!alive) return;
      const next = resolveBrandState({ host, response, cached });
      if (next.kind === "brand" && !next.stale) writeCache(key, serializeBrandForCache(next.brand));
      if (next.kind === "redirect" || (next.kind === "neutral" && response.status === "http" && response.httpStatus === 404)) {
        writeCache(key, null); // the API says this host is nobody's — forget any old brand
      }
      if (next.kind === "redirect") {
        window.location.replace(next.to);
        return;
      }
      setState(next);
    });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <BrandContext.Provider value={state}>
      <PlainDevHostContext.Provider value={plainDevHost}>
        <BrandHead state={state} />
        {children}
      </PlainDevHostContext.Provider>
    </BrandContext.Provider>
  );
}
